import React, { useState } from 'react';
import PropTypes from 'prop-types';

import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrFileUploader from 'client/components/SrFileUploader';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrCheckbox from 'client/components/SrCheckbox';
import {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';

import styles from './SrAdminUploadInsurancePolicyModal.less';

const CHECKBOXES = [
  {
    id: 'effectiveOnOrBeforeLease',
    label: 'Policy effective date is on or before the lease start date.',
  },
  {
    id: 'pmAdditionallyInsured',
    label: 'Property manager is listed as additionally insured.',
  },
  {
    id: 'coverageAtLeast100k',
    label: 'Liability coverage is at least $100,000.',
  },
];

const SrAdminUploadInsurancePolicyModal = ({
  leaseStartDate,
  propertyManagerName,
  utilityActivationId,
  utilityActivationItemId,
  onClose,
  onSubmit,
  uploadFn,
  fileUrl: initialFileUrl,
}) => {
  // Single source of truth for the uploaded file URL — wiring both
  // onFileUploaded and onFileRemoved here keeps remove → canSubmit=false
  // accurate. Previously this lived in parallel with a Formik field that
  // only updated on upload, so a remove after upload left a stale URL in
  // local state and the submit button enabled.
  const [fileUrl, setFileUrl] = useState(initialFileUrl);
  const [checked, setChecked] = useState(() =>
    Object.fromEntries(CHECKBOXES.map(({ id }) => [id, false])),
  );
  const [submitting, setSubmitting] = useState(false);

  const allChecked = CHECKBOXES.every(({ id }) => checked[id]);
  const canSubmit = !!fileUrl && allChecked && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit({ documentUrl: fileUrl });
      // On success the parent closes (unmounts) us; don't touch state here
      // to avoid setState-on-unmounted warnings.
    } catch (e) {
      // Parent already surfaced the error; just re-enable submit so the
      // admin can retry without re-selecting the file or rechecking boxes.
      setSubmitting(false);
    }
  };

  const filePath = `utility-activations/${utilityActivationId}/item-${utilityActivationItemId}/admin-external-policy-{uuid}`;

  return (
    <SrSimpleMessageModal
      theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
      maxWidth="480"
      onClose={onClose}
      headline={
        <SrText fontSize="22px" weight="800" font="greycliff" display="block">
          Upload policy on renter&rsquo;s behalf
        </SrText>
      }
      underline
      message={
        <SrBox maxWidth="400px" margin="0 auto">
          <SrBox marginBottom="12px">
            <SrText fontSize="16px" font="greycliff" display="block">
              Lease start date: <strong>{leaseStartDate}</strong>
            </SrText>
            <SrText fontSize="16px" font="greycliff" display="block">
              Property manager: <strong>{propertyManagerName}</strong>
            </SrText>
          </SrBox>

          <SrFileUploader
            value={fileUrl}
            accept=".pdf,.png,.jpg,.jpeg"
            filePath={filePath}
            uploadFn={uploadFn}
            onFileUploaded={({ s3FileUrl }) => setFileUrl(s3FileUrl)}
            onFileRemoved={() => setFileUrl(null)}
          />

          <SrBox marginTop="12px">
            <div className={styles.checkboxes}>
              {CHECKBOXES.map(({ id, label }) => (
                <SrCheckbox
                  key={id}
                  checked={checked[id]}
                  onClick={() => setChecked((prev) => ({ ...prev, [id]: !prev[id] }))}
                >
                  {label}
                </SrCheckbox>
              ))}
            </div>
          </SrBox>
        </SrBox>
      }
      srMultiButtons={[
        {
          buttonText: submitting ? 'Uploading…' : 'Upload policy',
          onButtonClick: handleSubmit,
          buttonTheme: SR_BUTTON_THEME_ADOBE_GREEN,
          buttonDisabled: !canSubmit,
        },
        {
          buttonText: 'Cancel',
          onButtonClick: onClose,
          buttonTheme: SR_BUTTON_THEME_ADOBE_GREY,
        },
      ]}
    />
  );
};

SrAdminUploadInsurancePolicyModal.propTypes = {
  leaseStartDate: PropTypes.string.isRequired,
  propertyManagerName: PropTypes.string.isRequired,
  utilityActivationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  utilityActivationItemId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  // Routes the file upload through core's USER_UPLOAD_BUCKET (via a presigned
  // PUT) instead of web's default bucket, so the download endpoint can serve it.
  uploadFn: PropTypes.func,
  fileUrl: PropTypes.string,
};

SrAdminUploadInsurancePolicyModal.defaultProps = {
  uploadFn: undefined,
  fileUrl: null,
};

SrAdminUploadInsurancePolicyModal.displayName = 'SrAdminUploadInsurancePolicyModal';

export default SrAdminUploadInsurancePolicyModal;
